// ============================================================================
// post-client-mod -- PostApiClient
//
// HTTP API 客户端，封装对 /api/posts/* 的调用。
//
// 支持两种使用方式：
// 1. 通过 IFetchService（由 DI 注入，浏览器/SSR 环境自动适配）
// 2. 直接使用（向后兼容，默认使用原生 fetch）
// ============================================================================
/**
 * 获取 auth token（从 localStorage）
 */
function getAuthToken() {
    if (typeof window === 'undefined')
        return null;
    return localStorage.getItem('accessToken');
}
/**
 * Post HTTP API 客户端
 *
 * 封装对 post-mod 提供的 REST API 调用。
 * 所有方法返回解析后的 JSON 数据，失败时抛出 Error。
 * 写操作自动附带 auth token。
 */
export class PostApiClient {
    /**
     * @param deps - 依赖对象（DI 注入）或直接传入 IFetchService 实例（兼容旧接口）
     *
     * 支持两种使用方式：
     * 1. DI 注入：new PostApiClient({ fetchService: instance })
     * 2. 直接构造：new PostApiClient() 或 new PostApiClient(instance)
     */
    constructor(deps) {
        if (deps && typeof deps === 'object' && 'fetchService' in deps) {
            this.fetchService = deps.fetchService ?? null;
        }
        else {
            this.fetchService = deps ?? null;
        }
    }
    /**
     * 使用 IFetchService 或原生 fetch 发起请求
     */
    async doFetch(url, options) {
        if (this.fetchService) {
            return this.fetchService.fetch(url, options);
        }
        const response = await globalThis.fetch(url, {
            method: options?.method ?? 'GET',
            headers: options?.headers,
            body: options?.body,
        });
        return {
            status: response.status,
            ok: response.ok,
            json: () => response.json(),
            text: () => response.text(),
        };
    }
    /**
     * 认证请求头（在有 token 时附带）
     */
    get authHeaders() {
        const headers = {};
        const token = getAuthToken();
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return headers;
    }
    // ========================================================================
    // 文章 CRUD
    // ========================================================================
    /**
     * 获取文章列表（分页 + 过滤）
     */
    async getPosts(params) {
        const searchParams = new URLSearchParams();
        if (params?.page)
            searchParams.set('page', String(params.page));
        if (params?.pageSize)
            searchParams.set('pageSize', String(params.pageSize));
        if (params?.status)
            searchParams.set('status', params.status);
        if (params?.authorId !== undefined)
            searchParams.set('authorId', String(params.authorId));
        if (params?.categorySlug)
            searchParams.set('categorySlug', params.categorySlug);
        if (params?.tagSlugs) {
            const tagSlugs = Array.isArray(params.tagSlugs) ? params.tagSlugs : [params.tagSlugs];
            for (const slug of tagSlugs) {
                searchParams.append('tagSlugs', slug);
            }
        }
        const queryStr = searchParams.toString();
        const url = `/api/posts${queryStr ? `?${queryStr}` : ''}`;
        const response = await this.doFetch(url, {
            method: 'GET',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to fetch posts: ${response.status}`);
        }
        return data;
    }
    /**
     * 获取单篇文章详情
     */
    async getPost(id) {
        const response = await this.doFetch(`/api/posts/${id}`, {
            method: 'GET',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to fetch post ${id}: ${response.status}`);
        }
        return data;
    }
    /**
     * 创建文章（需要认证）
     */
    async createPost(params) {
        const response = await this.doFetch('/api/posts', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...this.authHeaders },
            body: JSON.stringify(params),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to create post: ${response.status}`);
        }
        return data;
    }
    /**
     * 更新文章（需要认证）
     */
    async updatePost(id, params) {
        const response = await this.doFetch(`/api/posts/${id}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', ...this.authHeaders },
            body: JSON.stringify(params),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to update post ${id}: ${response.status}`);
        }
        return data;
    }
    /**
     * 删除文章（需要认证）
     */
    async deletePost(id) {
        const response = await this.doFetch(`/api/posts/${id}`, {
            method: 'DELETE',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to delete post ${id}: ${response.status}`);
        }
    }
    // ========================================================================
    // 搜索
    // ========================================================================
    /**
     * 搜索文章（关键词模糊匹配）
     */
    async searchPosts(params) {
        const searchParams = new URLSearchParams();
        searchParams.set('q', params.q);
        if (params.page)
            searchParams.set('page', String(params.page));
        if (params.pageSize)
            searchParams.set('pageSize', String(params.pageSize));
        if (params.categorySlug)
            searchParams.set('categorySlug', params.categorySlug);
        if (params.tagSlug)
            searchParams.set('tagSlugs', params.tagSlug);
        const url = `/api/posts/search?${searchParams.toString()}`;
        const response = await this.doFetch(url, {
            method: 'GET',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Search failed: ${response.status}`);
        }
        return data;
    }
    // ========================================================================
    // 分类/标签读取
    // ========================================================================
    /**
     * 获取分类列表（公开）
     */
    async getCategories() {
        const response = await this.doFetch('/api/posts/categories', {
            method: 'GET',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to fetch categories: ${response.status}`);
        }
        if (!Array.isArray(data)) {
            return [];
        }
        return data;
    }
    /**
     * 获取标签列表（公开）
     */
    async getTags() {
        const response = await this.doFetch('/api/posts/tags', {
            method: 'GET',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to fetch tags: ${response.status}`);
        }
        if (!Array.isArray(data)) {
            return [];
        }
        return data;
    }
    // ========================================================================
    // 分类管理（需要认证）
    // ========================================================================
    /**
     * 创建分类
     */
    async createCategory(params) {
        const response = await this.doFetch('/api/posts/categories', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...this.authHeaders },
            body: JSON.stringify(params),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to create category: ${response.status}`);
        }
        return data;
    }
    /**
     * 删除分类
     */
    async deleteCategory(slug) {
        const response = await this.doFetch(`/api/posts/categories/${encodeURIComponent(slug)}`, {
            method: 'DELETE',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to delete category: ${response.status}`);
        }
    }
    // ========================================================================
    // 标签管理（需要认证）
    // ========================================================================
    /**
     * 创建标签
     */
    async createTag(params) {
        const response = await this.doFetch('/api/posts/tags', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...this.authHeaders },
            body: JSON.stringify(params),
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to create tag: ${response.status}`);
        }
        return data;
    }
    /**
     * 删除标签
     */
    async deleteTag(slug) {
        const response = await this.doFetch(`/api/posts/tags/${encodeURIComponent(slug)}`, {
            method: 'DELETE',
            headers: this.authHeaders,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Failed to delete tag: ${response.status}`);
        }
    }
    // ========================================================================
    // 图片上传
    // ========================================================================
    /**
     * 上传图片（需要认证）
     */
    async uploadImage(file) {
        const formData = new FormData();
        formData.append('file', file);
        const response = await this.doFetch('/api/posts/upload', {
            method: 'POST',
            headers: this.authHeaders,
            body: formData,
        });
        const data = await response.json();
        if (!response.ok) {
            throw new Error(data.error || `Upload failed: ${response.status}`);
        }
        return data;
    }
}
/** 默认单例实例（向后兼容，使用原生 fetch） */
export const postApiClient = new PostApiClient();
// ============================================================================
// 容器感知的 PostApiClient（可选，由 spa-entry 在初始化时注入）
// ============================================================================
let _injectedClient = null;
/**
 * 注入 PostApiClient 实例（由 spa-entry 在容器加载后调用）
 */
export function setPostApiClient(client) {
    _injectedClient = client;
}
/**
 * 获取当前可用的 PostApiClient 实例
 * 优先返回注入的实例，回退到默认单例
 */
export function getPostApiClient() {
    return _injectedClient ?? postApiClient;
}
//# sourceMappingURL=post-api-client.js.map